import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { FronteggProvider, FronteggThemeOptions } from "@frontegg/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes
} from "react-router-dom";
import App from "./App";
import AuthWrapper from "@/services/auth/AuthWrapper";
import { AnalyticsProvider } from "./services/analytics/provider";
// Self-hosted fonts (Figtree among them) for SRI compliance (CWE-345)
import "@dreamhub/designsystem/fonts.css";
import "@dreamhub/designsystem/tokens.css";
import "./index.css";
import ScreenLoader from "@/components/shared/ScreenLoader.tsx";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import * as Sentry from "@sentry/react";
import { FeatureFlagsProvider } from "@/services/features/provider.tsx";
import { logBuildInfo } from "@/utils/build-info";
import { installNativeTextShortcuts } from "@/utils/textShortcuts/nativeTextShortcuts";

const isProduction = import.meta.env.VITE_DEPLOY_ENV === "prod";

// Arrow auto-replace (-> → →, <-> → ↔) across every plain input/textarea.
// Rich TipTap editors are handled by their own TextShortcuts extension.
installNativeTextShortcuts();

Sentry.init({
  dsn: "https://b2584aa11daa27551487a994e78706ee@o4509201481007104.ingest.de.sentry.io/4509201498898512",
  enabled: !import.meta.env.DEV,
  // Disable XHR instrumentation from Breadcrumbs and BrowserApiErrors — Sentry patches
  // XMLHttpRequest.prototype synchronously at startup, and Pendo detects the pre-modified
  // XHR when its Visual Design Studio loads, triggering a "Changes to Javascript Methods
  // Detected" alert. fetch breadcrumbs, tracing, and Replay are unaffected.
  integrations: (defaultIntegrations) => [
    ...defaultIntegrations.filter(
      (integration) => !["Breadcrumbs", "BrowserApiErrors"].includes(integration.name)
    ),
    Sentry.breadcrumbsIntegration({ xhr: false }),
    Sentry.browserApiErrorsIntegration({ XMLHttpRequest: false }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    // Only include replay integration in production to avoid interfering
    // with Vite HMR (MutationObserver + network patching breaks hot-reload)
    ...(isProduction
      ? [
          Sentry.replayIntegration({
            maskAllText: false,
            maskAllInputs: true,
            blockAllMedia: true,
            networkDetailAllowUrls: [],
            networkCaptureBodies: false,
            mutationBreadcrumbLimit: 100,
            mutationLimit: 500
          })
        ]
      : [])
  ],
  tracesSampleRate: isProduction ? 1.0 : 0,
  replaysSessionSampleRate: isProduction ? 0.1 : 0,
  replaysOnErrorSampleRate: isProduction ? 1.0 : 0,
  environment: import.meta.env.VITE_DEPLOY_ENV
});

logBuildInfo();

const contextOptions = {
  baseUrl: import.meta.env.VITE_FRONTEGG_BASE_URL,
  clientId: import.meta.env.VITE_FRONTEGG_CLIENT_ID
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30 // Garbage collect unused queries after 30 minutes
    }
  }
});

const themeOptions: FronteggThemeOptions = {
  palette: {
    background: { default: "#1c2432" }
  },
  loginBox: {
    themeName: "dark",
    poweredByColor: "#1c2432"
  }
};

const TriggerWhenMounted = ({
  children,
  onMounted
}: {
  children: React.ReactNode;
  onMounted: () => void;
}) => {
  useEffect(() => {
    onMounted();
  }, [onMounted]);
  return children;
};

const FronteggWrapper = () => {
  const [loading, setLoading] = useState(true);

  return (
    <>
      {loading && <ScreenLoader />}
      <FronteggProvider
        authOptions={{
          keepSessionAlive: true,
          hostedLoginOptions: {
            loadUserOnFirstLoad: false
          }
        }}
        contextOptions={contextOptions}
        hostedLoginBox={true}
        customLoader={setLoading}
        themeOptions={themeOptions}
      >
        <TriggerWhenMounted onMounted={() => setLoading(false)}>
          <AuthWrapper>
            <FeatureFlagsProvider>
              <App />
            </FeatureFlagsProvider>
          </AuthWrapper>
        </TriggerWhenMounted>
        {/* Only include DevTools in development to prevent memory accumulation in production */}
        {import.meta.env.DEV && (
          <div className="fixed bottom-16 left-5">
            <ReactQueryDevtools buttonPosition="relative" />
          </div>
        )}
      </FronteggProvider>
    </>
  );
};

const Root: React.FC = () => {
  return (
    <React.StrictMode>
      <AnalyticsProvider>
        <QueryClientProvider client={queryClient}>
          <FronteggWrapper />
        </QueryClientProvider>
      </AnalyticsProvider>
    </React.StrictMode>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(<Root />);
